/**
 * SharesManager.jsx — manage RotoTube client-delivery links.
 *
 * Lists every share with its status (active / expired / revoked), view count, and
 * a copy-link button; lets you revoke (kills the public link but keeps the row)
 * or delete. Opened from the Collections view header.
 */

import { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, Chip,
  IconButton, List, ListItem, CircularProgress, Tooltip,
} from '@mui/material';
import {
  ContentCopy as CopyIcon, Check as CheckIcon, Block as RevokeIcon, Delete as DeleteIcon,
  Visibility as ViewsIcon,
} from '@mui/icons-material';
import { useShares, useUpdateShare, useDeleteShare } from '../../hooks/useRotoTube';

const STATUS_COLOR = { active: '#4ade80', expired: '#facc15', revoked: '#f87171' };

export default function SharesManager({ open, onClose }) {
  const { data: shares, isLoading } = useShares();
  const updateShare = useUpdateShare();
  const deleteShare = useDeleteShare();
  const [copiedId, setCopiedId] = useState(null);

  const copy = async (share) => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}${share.path}`);
      setCopiedId(share.id); setTimeout(() => setCopiedId(null), 1800);
    } catch { /* ignore */ }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { bgcolor: '#212121', backgroundImage: 'none', color: '#f1f1f1' } }}>
      <DialogTitle>Client share links</DialogTitle>
      <DialogContent>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={26} /></Box>
        ) : !shares?.length ? (
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.55)', py: 2 }}>
            No client links yet. Share a collection or a video to create one.
          </Typography>
        ) : (
          <List sx={{ py: 0 }}>
            {shares.map((s) => (
              <ListItem key={s.id} sx={{ px: 0, py: 1.25, borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                    <Typography sx={{ fontWeight: 500, color: '#f1f1f1' }} noWrap>{s.title || 'Untitled share'}</Typography>
                    <Chip label={s.status} size="small" sx={{ height: 18, fontSize: '0.62rem', textTransform: 'uppercase', color: '#0f0f0f', bgcolor: STATUS_COLOR[s.status] || '#aaa', '& .MuiChip-label': { px: 0.75 } }} />
                  </Box>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    {s.clientName ? `${s.clientName} · ` : ''}{s.itemCount} {s.itemCount === 1 ? 'item' : 'items'}
                    <ViewsIcon sx={{ fontSize: 13, ml: 0.5 }} /> {s.viewCount}
                  </Typography>
                </Box>
                <Tooltip title="Copy link">
                  <IconButton size="small" onClick={() => copy(s)} sx={{ color: copiedId === s.id ? '#4ade80' : '#ddd' }}>
                    {copiedId === s.id ? <CheckIcon fontSize="small" /> : <CopyIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
                {s.status !== 'revoked' && (
                  <Tooltip title="Revoke">
                    <IconButton size="small" onClick={() => updateShare.mutate({ id: s.id, revoked: true })} sx={{ color: '#ddd' }}>
                      <RevokeIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Delete">
                  <IconButton
                    size="small"
                    onClick={() => {
                      if (window.confirm(`Delete "${s.title || 'this share'}"? The client link stops working immediately.`)) deleteShare.mutate(s.id);
                    }}
                    sx={{ color: '#ddd' }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="contained">Done</Button>
      </DialogActions>
    </Dialog>
  );
}
